/**
 * Match a loyalty package upgrade across an availability refresh after bedbank rejects its old price.
 *
 * Availability regenerates each `rateId`, while `service.rateId` remains stable. Resolve the target
 * relation to its service rate id before comparing the old and refreshed rates.
 */

type PropertyRatePricing = App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing']

export interface RefreshedPropertyRateUpgrade {
  sourcePricing: PropertyRatePricing
  targetPricing: PropertyRatePricing
}

function toPricing(rate: App.RawPropertyRate): PropertyRatePricing {
  return { totals: rate.totals, luxPlusTotals: rate.luxPlusTotals }
}

/**
 * Return the refreshed source and target rates when the same loyalty package upgrade still exists.
 * Return `undefined` if either rate is gone or the relation changed.
 *
 * Only `package` targets are supported. A future loyalty room-upgrade target must be handled
 * explicitly.
 */
export function findRefreshedPropertyRateUpgradeRates(
  upgrade: Pick<App.Checkout.PropertyRateLoyaltyUpgrade, 'source' | 'target'>,
  rooms: Array<App.RawPropertyRoomRates> | undefined,
): { sourceRate: App.RawPropertyRate, targetRate: App.RawPropertyRate } | undefined {
  if (!rooms?.length) return

  const rates = rooms.flatMap((room) => ('rates' in room ? Object.values(room.rates) : []))
  const sourceRate = rates.find((rate) => rate.service?.rateId === upgrade.source.serviceRateId)
  if (!sourceRate) return

  const targetRateId = sourceRate.relations?.upgradeTargets?.package
  if (!targetRateId) return

  const targetRate = rates.find((rate) => rate.rateId === targetRateId)
  if (!targetRate || targetRate.service?.rateId !== upgrade.target.serviceRateId) return

  return { sourceRate, targetRate }
}

/**
 * Return refreshed source and target pricing when the same loyalty package upgrade still exists.
 * Return `undefined` if either rate is gone or the relation changed.
 */
export default function findRefreshedPropertyRateUpgrade(
  upgrade: Pick<App.Checkout.PropertyRateLoyaltyUpgrade, 'source' | 'target'>,
  rooms: Array<App.RawPropertyRoomRates> | undefined,
): RefreshedPropertyRateUpgrade | undefined {
  const refreshed = findRefreshedPropertyRateUpgradeRates(upgrade, rooms)
  if (!refreshed) return

  return {
    sourcePricing: toPricing(refreshed.sourceRate),
    targetPricing: toPricing(refreshed.targetRate),
  }
}
